import {
  Flame,
  Clock3,
  Calendar,
  Mail,
  Snowflake,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react'
import type {
  EnrichedAction,
  SignalType,
} from '../../services/recommendations_api'

/**
 * Circle background and icon color for each urgency level.
 * Replaces the old risk_level-based RISK_STYLES — urgency now drives the circle color.
 */
export const URGENCY_CIRCLE_STYLES: Record<
  EnrichedAction['urgency'],
  { iconBg: string; iconColor: string }
> = {
  do_now: { iconBg: 'bg-error-bg', iconColor: 'text-error' },
  today: { iconBg: 'bg-yellow-50', iconColor: 'text-yellow-500' },
  this_week: { iconBg: 'bg-fb-green-50', iconColor: 'text-fb-dark-green' },
}

/**
 * Icon for each signal type. Used in action card circles and signal badges.
 */
export const SIGNAL_TYPE_ICONS: Record<SignalType, LucideIcon> = {
  unreplied_email: Mail,
  inactive_deal: Snowflake,
  email_stage_change: ArrowRight,
}

/**
 * Configuration for urgency level icons.
 */
export const URGENCY_ICONS = {
  do_now: Flame,
  today: Clock3,
  this_week: Calendar,
} as const

/**
 * Configuration for urgency level colors.
 */
export const URGENCY_COLORS = {
  do_now: 'text-error',
  today: 'text-yellow-500',
  this_week: 'text-fb-dark-green',
} as const

/**
 * Tooltip content for urgency levels.
 */
export const URGENCY_TOOLTIPS: Record<
  EnrichedAction['urgency'],
  { title: string; content: string }
> = {
  do_now: {
    title: 'Hot',
    content: 'Your highest-priority actions right now',
  },
  today: {
    title: 'Today',
    content: 'Not immediately urgent but still time-sensitive',
  },
  this_week: {
    title: 'This Week',
    content: 'Plan to complete within the next few days',
  },
}

/**
 * Number of items to show by default before requiring "Show More" button.
 */
export const DEFAULT_VISIBLE_ITEMS = 3

/**
 * Configuration for signal-based sections displayed in the UI.
 * Ordered by priority: unreplied emails first, then inactive deals.
 * Icons come from SIGNAL_TYPE_ICONS — one source of truth.
 */
export const SIGNAL_SECTIONS: Array<{
  key: SignalType
  title: string
  description: string
  Icon: LucideIcon
  iconColor: string
}> = [
  {
    key: 'unreplied_email',
    title: 'Unreplied Emails',
    description: 'Inbound messages from 3-60 days ago awaiting your response',
    Icon: SIGNAL_TYPE_ICONS.unreplied_email,
    iconColor: 'text-fg-secondary',
  },
  {
    key: 'inactive_deal',
    title: 'Inactive Deals',
    description:
      'Deals needing attention (7-90 days stale or 14+ days in same stage)',
    Icon: SIGNAL_TYPE_ICONS.inactive_deal,
    iconColor: 'text-fg-secondary',
  },
  {
    key: 'email_stage_change',
    title: 'Update Deal Stage',
    description:
      'Deals where recent inbound emails suggest a stage advancement is warranted',
    Icon: SIGNAL_TYPE_ICONS.email_stage_change,
    iconColor: 'text-fg-secondary',
  },
]

/**
 * Tooltip content for signal types.
 */
export const SIGNAL_TOOLTIPS: Record<
  SignalType,
  { title: string; content: string }
> = {
  unreplied_email: {
    title: 'Unreplied Email',
    content:
      'Inbound emails from 3-60 days ago awaiting your response. Automated emails (shipping, receipts, etc.) are filtered out.',
  },
  inactive_deal: {
    title: 'Inactive Deal',
    content:
      'Deals with no activity in 7+ days OR stuck in the same stage for 14+ days. Limited to deals active within the last 90 days.',
  },
  email_stage_change: {
    title: 'Update Deal Stage',
    content:
      'A recent inbound email suggests this deal may be ready to advance to the next stage. Review and apply the AI-recommended stage change.',
  },
}

/**
 * Icon colors for each signal type.
 * Used consistently across signal badges and company sections.
 */
export const SIGNAL_ICON_COLORS: Record<SignalType, string> = {
  unreplied_email: 'text-fg-secondary',
  inactive_deal: 'text-fg-secondary',
  email_stage_change: 'text-fg-secondary',
}

/**
 * Format a signal-specific label showing the days metric.
 *
 * @param signalType - The type of signal
 * @param daysUnreplied - Days since email received (for unreplied_email)
 * @param daysStale - Days since deal activity (for inactive_deal)
 * @param daysInStage - Days in current stage (for inactive_deal)
 * @param currentStage - Current sales stage name
 * @returns Formatted label string (e.g., "5d unreplied", "12d inactive")
 */
export const formatSignalLabel = (
  signalType: SignalType,
  daysUnreplied: number | null,
  daysStale: number | null,
  daysInStage: number | null,
  currentStage: string | null
): string => {
  switch (signalType) {
    case 'unreplied_email':
      return `${daysUnreplied ?? 0}d unreplied`
    case 'inactive_deal': {
      // Show the most relevant metric
      if (daysInStage && daysInStage >= 14) {
        return `${daysInStage}d in ${currentStage || 'stage'}`
      }
      return `${daysStale ?? 0}d inactive`
    }
    case 'email_stage_change':
      return `${daysInStage ?? 0}d in ${currentStage || 'stage'}`
    default:
      return ''
  }
}
